// ============================================================================
// ItemPassives.js - Complete Item Passive Definitions
// ============================================================================
// Parsed from FS_ROV_Database Excel Column 7 (สกิลติดตัว)
// All 70 items with passives are defined here
// ============================================================================

import { DamageType, CCType, BuffType, TICKS_PER_SECOND } from './CombatEngine.js';
import { inferItemPassiveFromText } from './ExcelTextParsers';

// ============================================================================
// ITEM PASSIVE DEFINITIONS
// ============================================================================

export const ITEM_PASSIVES = {

  // ════════════════════════════════════════════════════════════════════════
  // ATTACK ITEMS
  // ════════════════════════════════════════════════════════════════════════

  // ── Tier 2 Attack ──

  'Arcane Hammer': {
    name: 'Speed Up',
    trigger: 'ON_HIT',
    effect: 'STAT_BUFF',
    description: 'AA grants +10% MS',
    apply(actor, target, tick, log, state) {
      actor.buffs = actor.buffs.filter(b => b.source !== 'Arcane Hammer');
      actor.buffs.push({
        type: BuffType.STAT_PCT,
        stat: 'movement_speed',
        value: 0.10,
        name: 'Speed Up',
        source: 'Arcane Hammer',
        remainingTicks: 20, // ~2s
      });
    },
  },

  'Shuriken': {
    name: 'Heel',
    trigger: 'ON_HIT',
    effect: 'BONUS_PHYSICAL_DAMAGE',
    cooldown: 0.3,
    description: 'AA deals bonus physical 21-35 (melee) / 42-70 (ranged)',
    apply(actor, target, tick, log, state) {
      const isRanged = actor.heroData?.attack_type === 'RANGED';
      const lvl = actor.level || 1;
      const baseDmg = isRanged ? (42 + (lvl - 1) * 2) : (21 + (lvl - 1) * 1);
      return { bonusDamage: baseDmg, damageType: DamageType.PHYSICAL };
    },
  },

  'Bloodthirsty Blade': {
    name: 'Blood Feast',
    trigger: 'CONDITIONAL',
    condition: 'hp_below_30',
    cooldown: 20,
    description: 'Below 30% HP: heal 400-610 over 5s',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.3 && !state.onCD) {
        const healTotal = 400 + actor.level * 14;
        actor.buffs.push({
          type: BuffType.HOT,
          name: 'Blood Feast',
          source: 'Bloodthirsty Blade',
          healPerTick: Math.round(healTotal / 50), // over 5s = 50 ticks
          tickInterval: 1,
          remainingTicks: 50,
        });
        state.onCD = true;
        state.cooldown = 20 * TICKS_PER_SECOND;
      }
    },
  },

  'Chaos Bow': {
    name: 'Stab',
    trigger: 'ALWAYS',
    effect: 'STAT_BUFF',
    description: 'Armor Pierce +10% (ranged heroes get 2x = +20%)',
    apply(actor) {
      const isRanged = actor.heroData?.attack_type === 'RANGED';
      return { statBuff: { armor_pierce_pct: isRanged ? 0.20 : 0.10 } };
    },
  },

  "Uriel's Brand": {
    name: "Savior's Edge",
    trigger: 'CONDITIONAL',
    condition: 'hp_below_40',
    cooldown: 0, // not specified in data
    description: 'Below 40% HP: shield (600-2000) for 5s, +25% resistance, +30% MS for 2s',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.4 && !state.triggered) {
        const shieldAmt = 600 + actor.level * 100;
        actor.shields.push({
          amount: shieldAmt,
          source: "Uriel's Brand",
          remainingTicks: 50,
        });
        actor.buffs.push({
          type: BuffType.STAT_PCT,
          stat: 'movement_speed',
          value: 0.30,
          name: 'Savior MS',
          source: "Uriel's Brand",
          remainingTicks: 20,
        });
        state.triggered = true;
      }
    },
  },

  'Curse of Death': {
    name: 'Soul Prison',
    trigger: 'ON_HIT',
    effect: 'DEBUFF',
    description: 'Reduce target healing by 40% for 2s (AA: 4s)',
    apply(actor, target, tick, log, state, triggerType) {
      const dur = triggerType === 'AA' ? 4 : 2;
      target.debuffs = target.debuffs.filter(d => d.source !== 'Curse of Death');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'healing_received',
        value: 0.40,
        name: 'Soul Prison',
        source: 'Curse of Death',
        remainingTicks: dur * TICKS_PER_SECOND,
      });
    },
  },

  'Death Sickle': {
    name: 'The Insatiable',
    trigger: 'CONDITIONAL',
    condition: 'hp_low',
    description: 'When low HP: block damage briefly, +25% MS and AS for 0.75s',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.3 && !state.triggered) {
        actor.buffs.push({
          type: BuffType.INVULNERABLE,
          name: 'Insatiable Shield',
          source: 'Death Sickle',
          remainingTicks: 8, // ~0.75s
        });
        actor.buffs.push({
          type: BuffType.STAT_PCT,
          stat: 'attack_speed',
          value: 0.25,
          name: 'Insatiable AS',
          source: 'Death Sickle',
          remainingTicks: 8,
        });
        state.triggered = true;
      }
    },
  },

  'Bow of Slaughter': {
    name: 'Bloodthirst',
    trigger: 'ACTIVE',
    cooldown: 60,
    description: '[Active] +60% Life Steal for 3s (ranged only)',
    active: {
      effect: 'LIFESTEAL_BOOST',
      lifeStealBonus: 0.60,
      duration: 3,
      cooldown: 60,
      name: 'Bloodthirst',
    },
  },

  'The Diminisher': {
    name: 'Sturdy',
    trigger: 'ACTIVE',
    cooldown: 90,
    description: '[Active] -40% damage received for 2s, usable while CC\'d',
    active: {
      effect: 'DAMAGE_REDUCTION',
      value: 0.40,
      duration: 2,
      cooldown: 90,
      name: 'Sturdy',
    },
    passive2: {
      name: 'Cripple',
      trigger: 'ON_SKILL_HIT',
      description: 'Skill hit reduces enemy MS',
    },
  },

  'Eclipsing Bow': {
    name: 'Reach',
    trigger: 'ACTIVE',
    cooldown: 60,
    description: '[Active] +150 range, +20% MS for 5s (ranged only)',
    active: {
      effect: 'STAT_BUFF',
      statBuffs: { movement_speed_pct: 0.20 },
      duration: 5,
      cooldown: 60,
      name: 'Reach',
    },
  },

  // ── Tier 3 Attack ──

  'Spear of Longinus': {
    name: 'Shatter',
    trigger: 'ON_HIT',
    cooldown: 0.3,
    description: 'Attacks reduce target armor by 6% per stack (max 4, 5s). Also +60 flat pen.',
    apply(actor, target, tick, log, state) {
      state.stacks = Math.min((state.stacks || 0) + 1, 4);
      target.debuffs = target.debuffs.filter(d => d.source !== 'Spear of Longinus');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'phys_defense',
        value: 0.06 * state.stacks,
        name: `Shatter x${state.stacks}`,
        source: 'Spear of Longinus',
        remainingTicks: 50,
      });
      state.cooldown = 3; // 0.3s = 3 ticks
    },
  },

  "Fafnir's Talon": {
    name: "Dragon's Breath",
    trigger: 'ON_HIT',
    cooldown: 0.3,
    description: 'AA deals bonus physical = 8% of target current HP',
    apply(actor, target, tick, log, state) {
      const bonusDmg = Math.round(target.currentHP * 0.08);
      return { bonusDamage: bonusDmg, damageType: DamageType.PHYSICAL };
    },
  },

  'Claves Sancti': {
    name: 'Claves Sancti',
    trigger: 'ALWAYS',
    description: 'Crit +25%, Crit DMG +40%',
    // Stats handled in item stats, no special logic needed
    apply() { return null; },
  },

  'Muramasa': {
    name: 'Stab',
    trigger: 'ALWAYS',
    description: 'Armor Pierce +40%',
    // Stats handled in item stats
    apply() { return null; },
  },

  'The Beast': {
    name: 'Blood Feast',
    trigger: 'CONDITIONAL',
    condition: 'hp_below_30',
    cooldown: 20,
    description: 'Below 30% HP: heal 400-610 over 5s',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.3 && state.cooldown <= 0) {
        const healTotal = 400 + actor.level * 14;
        actor.buffs.push({
          type: BuffType.HOT,
          name: 'Blood Feast',
          source: 'The Beast',
          healPerTick: Math.round(healTotal / 50),
          tickInterval: 1,
          remainingTicks: 50,
        });
        state.cooldown = 20 * TICKS_PER_SECOND;
      }
    },
  },

  'Omni Arms': {
    name: 'Five Forces',
    trigger: 'AFTER_SKILL',
    cooldown: 2,
    description: 'After skill: next AA deals +100% base AD bonus physical damage. Also +10% MS on AA.',
    nextAABonus: {
      damage: {
        base: 0,
        adScaling: 1.0, // +100% AD
        apScaling: 0,
        damageType: DamageType.PHYSICAL,
      },
      cc: null,
    },
    apply(actor, target, tick, log, state) {
      // Handled by AFTER_SKILL trigger in engine
    },
  },

  "Slikk's Sting": {
    name: 'Wind Bless',
    trigger: 'ON_CRIT',
    description: 'After crit: +35% status resistance for 2s',
    apply(actor, target, tick, log, state) {
      actor.buffs = actor.buffs.filter(b => b.source !== "Slikk's Sting");
      actor.buffs.push({
        type: BuffType.DAMAGE_REDUCTION,
        value: 0.05, // Simplified: resistance as minor DR
        name: 'Wind Bless',
        source: "Slikk's Sting",
        remainingTicks: 20,
      });
    },
  },

  "Fenrir's Tooth": {
    name: "Fenrir's Tooth",
    trigger: 'CONDITIONAL',
    condition: 'target_hp_below_50',
    description: '+30% damage when target HP < 50%',
    // Handled directly in damage pipeline (CombatEngine.js Stage 2)
    apply() { return null; },
  },

  'Blitz Blade': {
    name: 'Chain Lightning',
    trigger: 'ON_HIT',
    description: 'AA deals bonus magic 45-87. Every 3rd AA fires Chain Lightning (3 bounces)',
    apply(actor, target, tick, log, state) {
      const lvl = actor.level || 1;
      const baseMagic = 45 + (lvl - 1) * 3;
      state.hitCount = (state.hitCount || 0) + 1;
      
      let totalBonus = baseMagic;
      if (state.hitCount % 3 === 0) {
        totalBonus += baseMagic * 2; // Chain lightning = 3 bounces
        log.addEvent(tick, 'ITEM_PROC', {
          hero: actor.heroName,
          item: 'Blitz Blade',
          effect: 'Chain Lightning!',
        });
      }
      return { bonusDamage: totalBonus, damageType: DamageType.MAGIC };
    },
  },

  "Ifrit's Claw": {
    name: "Flame's Wrath",
    trigger: 'ON_HIT',
    description: 'AA grants +10% AS per stack, max 5 stacks, 2s duration',
    apply(actor, target, tick, log, state) {
      state.stacks = Math.min((state.stacks || 0) + 1, 5);
      actor.buffs = actor.buffs.filter(b => b.source !== "Ifrit's Claw");
      actor.buffs.push({
        type: BuffType.STAT_PCT,
        stat: 'attack_speed',
        value: 0.10 * state.stacks,
        name: `Flame's Wrath x${state.stacks}`,
        source: "Ifrit's Claw",
        remainingTicks: 20,
      });
    },
  },

  'Broken Spears': {
    name: 'Wind Walk + Destroyer',
    trigger: 'ALWAYS',
    description: '+10% MS out of combat, +40-180 flat pen (scales with level)',
    apply(actor) {
      const lvl = actor.level || 1;
      const flatPen = 40 + (lvl - 1) * 10;
      return { statBuff: { armor_pierce: flatPen } };
    },
  },

  'Frost Hammer': {
    name: 'Frostbite + Scar',
    trigger: 'ON_HIT',
    cooldown: 3,
    description: 'AA/Skill slow 5-10% (2 stacks). Bonus physical 140-280 (+45% bonus AD) on hit (CD 3s)',
    apply(actor, target, tick, log, state) {
      // Scar damage
      const lvl = actor.level || 1;
      const scarDmg = 140 + lvl * 10 + (actor.totalStats?.phys_atk || 0) * 0.45;
      
      // Frostbite slow
      state.slowStacks = Math.min((state.slowStacks || 0) + 1, 2);
      target.debuffs = target.debuffs.filter(d => d.source !== 'Frost Hammer Slow');
      target.debuffs.push({
        type: BuffType.CC,
        ccType: CCType.SLOW,
        value: 0.05 * state.slowStacks,
        name: `Frostbite x${state.slowStacks}`,
        source: 'Frost Hammer Slow',
        remainingTicks: 15,
      });
      
      state.cooldown = 3 * TICKS_PER_SECOND;
      return { bonusDamage: scarDmg, damageType: DamageType.PHYSICAL };
    },
  },

  'Morning Star': {
    name: 'Dawning Star + Stab',
    trigger: 'ON_HIT',
    description: 'AA deals +50 bonus physical (x2 for ranged). +20% armor pen.',
    apply(actor, target, tick, log, state) {
      const isRanged = actor.heroData?.attack_type === 'RANGED';
      const bonus = isRanged ? 100 : 50;
      return { bonusDamage: bonus, damageType: DamageType.PHYSICAL };
    },
  },

  // ════════════════════════════════════════════════════════════════════════
  // MAGIC ITEMS
  // ════════════════════════════════════════════════════════════════════════

  // ── Tier 2 Magic ──

  'Trick Blade': {
    name: 'Elemental Edge',
    trigger: 'AFTER_SKILL',
    cooldown: 3,
    description: 'After skill: next AA deals bonus magic (30% AD + 18% AP)',
    nextAABonus: {
      damage: {
        base: 0,
        adScaling: 0.30,
        apScaling: 0.18,
        damageType: DamageType.MAGIC,
      },
      cc: null,
    },
  },

  "Phoenix's Tear": {
    name: 'From the Ashes',
    trigger: 'ON_LEVEL_UP',
    description: 'On level up: restore 10% mana and 20% HP over 3s',
    // Not relevant in 1v1 combat simulation (no leveling during fight)
    apply() { return null; },
  },

  "Vlad's Impaler": {
    name: "Spell Vamp +15%",
    trigger: 'ALWAYS',
    description: 'Magic Life Steal +15%',
    // Stats handled in item stats
    apply() { return null; },
  },

  'Staff of Nuul': {
    name: 'Magic Pierce +40%',
    trigger: 'ALWAYS',
    description: 'Magic Pierce +40%',
    // Stats handled in item stats
    apply() { return null; },
  },

  "Vlad's Necklace": {
    name: 'Enlightened',
    trigger: 'PERIODIC',
    tickInterval: 10, // every 1s
    description: 'Restore 1% mana per second',
    apply(actor) {
      const manaRestore = Math.round(actor.maxMana * 0.01);
      actor.currentMana = Math.min(actor.maxMana, actor.currentMana + manaRestore);
    },
  },

  'Orrery': {
    name: 'Echo',
    trigger: 'ON_SKILL_HIT',
    cooldown: 5,
    description: 'Skill hit causes explosion: 180 magic damage',
    apply(actor, target, tick, log, state) {
      return { bonusDamage: 180, damageType: DamageType.MAGIC };
    },
  },

  'Evil Secrets': {
    name: 'Curse',
    trigger: 'ON_SKILL_HIT',
    description: 'Skill hit reduces target AP by 10% and Magic Def by 100 for 3s',
    apply(actor, target, tick, log, state) {
      target.debuffs = target.debuffs.filter(d => d.source !== 'Evil Secrets');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'magic_power',
        value: 0.10,
        name: 'Curse (AP)',
        source: 'Evil Secrets',
        remainingTicks: 30,
      });
      target.debuffs.push({
        type: BuffType.STAT_FLAT,
        stat: 'magic_defense',
        value: 100,
        name: 'Curse (MDef)',
        source: 'Evil Secrets',
        remainingTicks: 30,
      });
    },
  },

  'Soul Scroll': {
    name: 'Soul Prison',
    trigger: 'ON_HIT',
    description: 'Damage reduces target healing by 40% for 1.5s (AA: extends)',
    apply(actor, target, tick, log, state) {
      target.debuffs = target.debuffs.filter(d => d.source !== 'Soul Scroll');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'healing_received',
        value: 0.40,
        name: 'Soul Prison',
        source: 'Soul Scroll',
        remainingTicks: 15,
      });
    },
  },

  "Carano's Pledge": {
    name: 'Spare Life',
    trigger: 'CONDITIONAL',
    condition: 'hp_below_30',
    description: 'Below 30% HP: cleanse CC, shield 510-1000 (+100% bonus AP + 15% max HP)',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.3 && !state.triggered) {
        // Cleanse CC
        actor.debuffs = actor.debuffs.filter(d => d.type !== BuffType.CC);
        actor.isStunned = false;
        actor.isSilenced = false;
        
        const shieldAmt = 510 + (actor.totalStats?.magic_power || 0) + actor.maxHP * 0.15;
        actor.shields.push({
          amount: shieldAmt,
          source: "Carano's Pledge",
          remainingTicks: 40,
        });
        state.triggered = true;
        
        log.addEvent(tick, 'ITEM_PROC', {
          hero: actor.heroName,
          item: "Carano's Pledge",
          effect: `Spare Life! Shield ${Math.round(shieldAmt)}`,
        });
      }
    },
  },

  // ── Tier 3 Magic ──

  'Boomstick': {
    name: 'Echo',
    trigger: 'ON_SKILL_HIT',
    cooldown: 5,
    description: 'Skill hit causes explosion: 120 + 40% bonus AP magic damage',
    apply(actor, target, tick, log, state) {
      const bonusAP = actor.totalStats?.magic_power || 0;
      const dmg = 120 + bonusAP * 0.40;
      return { bonusDamage: dmg, damageType: DamageType.MAGIC };
    },
  },

  "Hecate's Diadem": {
    name: 'Kich',
    trigger: 'ALWAYS',
    description: 'Magic Power +35%',
    // This is a percentage increase on total AP
    // Handled specially in stats computation
    applyToStats(stats) {
      stats.magic_power *= 1.35;
      return stats;
    },
    apply() { return null; },
  },

  'Orb of the Magi': {
    name: 'Bide + From the Ashes',
    trigger: 'PERIODIC',
    description: 'Every 30s: +12 AP, +120 max HP (max 10 stacks). Level up: restore MP/HP.',
    // In combat sim, pre-compute stacks based on game time
    apply(actor, target, tick, log, state) {
      // Stacks are pre-computed, not relevant in short combat
      return null;
    },
  },

  'Zweihander': {
    name: 'Sudden Strike + Scorching',
    trigger: 'ON_HIT',
    cooldown: 10,
    description: 'AA grants +15% AS for 4s (max 2 stacks). AA deals bonus magic (2% target max HP)',
    apply(actor, target, tick, log, state) {
      // AS buff
      state.stacks = Math.min((state.stacks || 0) + 1, 2);
      actor.buffs = actor.buffs.filter(b => b.source !== 'Zweihander');
      actor.buffs.push({
        type: BuffType.STAT_PCT,
        stat: 'attack_speed',
        value: 0.15 * state.stacks,
        name: `Sudden Strike x${state.stacks}`,
        source: 'Zweihander',
        remainingTicks: 40,
      });
      
      // Scorching damage
      const bonusDmg = target.maxHP * 0.02;
      return { bonusDamage: bonusDmg, damageType: DamageType.MAGIC };
    },
  },

  "Frosty's Revenge": {
    name: 'Frost',
    trigger: 'ON_SKILL_HIT',
    description: 'Skill damage slows target 20% for 2s',
    apply(actor, target, tick, log, state) {
      target.debuffs = target.debuffs.filter(d => d.source !== "Frosty's Revenge");
      target.debuffs.push({
        type: BuffType.CC,
        ccType: CCType.SLOW,
        value: 0.20,
        name: 'Frost',
        source: "Frosty's Revenge",
        remainingTicks: 20,
      });
    },
  },

  "Berith's Agony": {
    name: 'Torture',
    trigger: 'ON_SKILL_HIT',
    description: 'Skill hit applies DoT: 80 + 2% target max HP magic per second for 3s',
    apply(actor, target, tick, log, state) {
      const dotPerSec = 80 + target.maxHP * 0.02;
      target.debuffs = target.debuffs.filter(d => d.source !== "Berith's Agony");
      target.debuffs.push({
        type: BuffType.DOT,
        name: 'Torture',
        source: "Berith's Agony",
        damagePerTick: Math.round(dotPerSec / TICKS_PER_SECOND),
        damageType: DamageType.MAGIC,
        tickInterval: 1,
        remainingTicks: 30,
      });
    },
  },

  'Apocalypse': {
    name: 'Elemental Edge',
    trigger: 'AFTER_SKILL',
    cooldown: 2,
    description: 'After skill: next AA deals bonus magic (30% bonus AD + 80% bonus AP)',
    nextAABonus: {
      damage: {
        base: 0,
        adScaling: 0.30,
        apScaling: 0.80,
        damageType: DamageType.MAGIC,
      },
      cc: null,
    },
  },

  'Soaring Aura': {
    name: 'Enchantment',
    trigger: 'ON_HIT',
    description: 'AA or Skill hit grants +10-24 magic pen per stack (max 10, 3s)',
    apply(actor, target, tick, log, state) {
      const lvl = actor.level || 1;
      const penPerStack = 10 + Math.floor(lvl / 2);
      state.stacks = Math.min((state.stacks || 0) + 1, 10);
      actor.buffs = actor.buffs.filter(b => b.source !== 'Soaring Aura');
      actor.buffs.push({
        type: BuffType.STAT_FLAT,
        stat: 'magic_pierce',
        value: penPerStack * state.stacks,
        name: `Enchantment x${state.stacks}`,
        source: 'Soaring Aura',
        remainingTicks: 30,
      });
    },
  },

  'Holy of Holies': {
    name: 'Enlightenment',
    trigger: 'ALWAYS',
    description: 'Every 100 AP: +0.5% damage dealt, -0.5% damage received',
    applyToStats(stats) {
      const ap = stats.magic_power || 0;
      const stacks = Math.floor(ap / 100);
      return {
        ...stats,
        _damageAmp: stacks * 0.005,
        _damageReduction: stacks * 0.005,
      };
    },
    apply() { return null; },
  },

  'Arctic Orb': {
    name: 'Solidify',
    trigger: 'ACTIVE',
    cooldown: 90,
    description: '[Active] Invulnerable 2s (cannot move/attack). After: shield 500 + Lv*40',
    active: {
      effect: 'INVULNERABLE',
      duration: 2,
      cooldown: 90,
      name: 'Solidify',
      afterShield: true,
      shieldBase: 500,
      shieldPerLevel: 40,
    },
  },

  'Frostguard': {
    name: 'Ice Armor',
    trigger: 'ALWAYS',
    description: 'Grants bonus armor and magic resist = 50% bonus AP (max 230)',
    applyToStats(stats) {
      const bonusAP = stats.magic_power || 0;
      const bonus = Math.min(bonusAP * 0.50, 230);
      stats.phys_defense += bonus;
      stats.magic_defense += bonus;
      return stats;
    },
    apply() { return null; },
  },

  'Mega Robe': {
    name: 'Replenishment + Magic Pierce',
    trigger: 'ALWAYS',
    description: 'AP 26-40 (+2% bonus HP, max 100). Magic Pierce 32-56.',
    applyToStats(stats) {
      const bonusHP = stats.hp || 0;
      const bonusAP = Math.min(26 + bonusHP * 0.02, 100);
      stats.magic_power += bonusAP;
      const lvl = 15; // default max level
      stats.magic_pierce += 32 + lvl * 1.6;
      return stats;
    },
    apply() { return null; },
  },

  "Rhea's Blessing": {
    name: "Rhea's Blessing",
    trigger: 'CONDITIONAL',
    description: 'Shield when HP is low (data incomplete in Excel)',
    apply() { return null; },
  },

  // ════════════════════════════════════════════════════════════════════════
  // DEFENSE ITEMS
  // ════════════════════════════════════════════════════════════════════════

  // ── Tier 2 Defense ──

  'Heart of Incubus': {
    name: 'Incantation',
    trigger: 'PERIODIC',
    tickInterval: 10, // every 1s
    description: 'Deal magic damage = 50-78 per second to nearby enemies',
    apply(actor, target, tick, log, state) {
      const lvl = actor.level || 1;
      const dmg = 50 + lvl * 2;
      return { bonusDamage: dmg, damageType: DamageType.MAGIC };
    },
  },

  'Belt of Clarity': {
    name: 'Restore',
    trigger: 'ON_DAMAGE_TAKEN',
    cooldown: 15,
    description: 'When taking damage: heal 4% max HP over 2s',
    apply(actor, target, tick, log, state) {
      if (state.cooldown <= 0) {
        const healTotal = actor.maxHP * 0.04;
        actor.buffs.push({
          type: BuffType.HOT,
          name: 'Restore',
          source: 'Belt of Clarity',
          healPerTick: Math.round(healTotal / 20),
          tickInterval: 1,
          remainingTicks: 20,
        });
        state.cooldown = 15 * TICKS_PER_SECOND;
      }
    },
  },

  "Knight's Plate": {
    name: 'Spirit Bond',
    trigger: 'ON_DAMAGE_TAKEN',
    description: 'When hit: reduce attacker AS by 15%',
    apply(actor, target, tick, log, state) {
      // target = attacker in this context
      target.debuffs = target.debuffs.filter(d => d.source !== "Knight's Plate");
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'attack_speed',
        value: 0.15,
        name: 'Spirit Bond',
        source: "Knight's Plate",
        remainingTicks: 20,
      });
    },
  },

  'Mail of Pain': {
    name: 'Blitz',
    trigger: 'ON_DAMAGE_TAKEN',
    description: 'Reflect physical damage back to attacker',
    // Handled directly in CombatEngine applyDamageResult
    apply() { return null; },
  },

  "Hercules's Madness": {
    name: 'Berserk',
    trigger: 'CONDITIONAL',
    condition: 'hp_below_40',
    cooldown: 60,
    description: 'Below 40% HP: +20% life steal, shield (800 + Lv*80) for 8s',
    apply(actor, target, tick, log, state) {
      if (actor.currentHP / actor.maxHP < 0.4 && state.cooldown <= 0) {
        actor.buffs.push({
          type: BuffType.STAT_FLAT,
          stat: 'life_steal',
          value: 0.20,
          name: 'Berserk LS',
          source: "Hercules's Madness",
          remainingTicks: 80,
        });
        const shieldAmt = 800 + actor.level * 80;
        actor.shields.push({
          amount: shieldAmt,
          source: "Hercules's Madness",
          remainingTicks: 80,
        });
        state.cooldown = 60 * TICKS_PER_SECOND;
      }
    },
  },

  "Odin's Will": {
    name: 'Loyalty',
    trigger: 'ON_DAMAGE_TAKEN',
    description: 'When hit: +1% MS and +2% AD per stack (max 5)',
    apply(actor, target, tick, log, state) {
      state.stacks = Math.min((state.stacks || 0) + 1, 5);
      actor.buffs = actor.buffs.filter(b => b.source !== "Odin's Will");
      actor.buffs.push({
        type: BuffType.STAT_PCT,
        stat: 'movement_speed',
        value: 0.01 * state.stacks,
        name: `Loyalty MS x${state.stacks}`,
        source: "Odin's Will",
        remainingTicks: 30,
      });
      actor.buffs.push({
        type: BuffType.STAT_PCT,
        stat: 'phys_atk',
        value: 0.02 * state.stacks,
        name: `Loyalty AD x${state.stacks}`,
        source: "Odin's Will",
        remainingTicks: 30,
      });
    },
  },

  // ── Tier 3 Defense ──

  'Mantle of Ra': {
    name: 'Incantation',
    trigger: 'PERIODIC',
    tickInterval: 10, // every 1s
    description: 'Deal magic damage = 2% max HP per second to nearby enemies',
    apply(actor, target, tick, log, state) {
      const dmg = actor.maxHP * 0.02;
      return { bonusDamage: dmg, damageType: DamageType.MAGIC };
    },
  },

  'Shield of the Lost': {
    name: 'Sentinel',
    trigger: 'ON_DAMAGE_TAKEN',
    description: 'Reduce attacker AS by 30% and MS by 15% for 2s',
    apply(actor, target, tick, log, state) {
      target.debuffs = target.debuffs.filter(d => d.source !== 'Shield of the Lost');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'attack_speed',
        value: 0.30,
        name: 'Sentinel AS',
        source: 'Shield of the Lost',
        remainingTicks: 20,
      });
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'movement_speed',
        value: 0.15,
        name: 'Sentinel MS',
        source: 'Shield of the Lost',
        remainingTicks: 20,
      });
    },
  },

  'The Aegis': {
    name: 'Sentinel',
    trigger: 'AURA',
    description: 'Nearby enemies: -30% AS',
    apply(actor, target, tick, log, state) {
      target.debuffs = target.debuffs.filter(d => d.source !== 'The Aegis');
      target.debuffs.push({
        type: BuffType.STAT_PCT,
        stat: 'attack_speed',
        value: 0.30,
        name: 'Aegis Sentinel',
        source: 'The Aegis',
        remainingTicks: 15, // Refreshed every tick
      });
    },
  },

  "Gaia's Standard": {
    name: 'Restore + Bloodline',
    trigger: 'ON_DAMAGE_TAKEN',
    cooldown: 10,
    description: 'When taking damage: heal 4% max HP over 2s. Also +bonus for each 10% HP lost.',
    apply(actor, target, tick, log, state) {
      if (state.cooldown <= 0) {
        const healTotal = actor.maxHP * 0.04;
        actor.buffs.push({
          type: BuffType.HOT,
          name: 'Restore',
          source: "Gaia's Standard",
          healPerTick: Math.round(healTotal / 20),
          tickInterval: 1,
          remainingTicks: 20,
        });
        state.cooldown = 10 * TICKS_PER_SECOND;
      }
    },
  },

  'Medallion of Troy': {
    name: 'Fairy + Harmony',
    trigger: 'CONDITIONAL',
    description: 'Out of combat: magic shield (400 + Lv*60). Also bonus magic def 8-112.',
    apply(actor, target, tick, log, state) {
      if (!state.shieldApplied) {
        const shieldAmt = 400 + actor.level * 60;
        actor.shields.push({
          amount: shieldAmt,
          source: 'Medallion of Troy',
          remainingTicks: 999, // Until broken
          permanent: true,
          magicOnly: true,
        });
        state.shieldApplied = true;
      }
    },
  },

  'Blade of Eternity': {
    name: 'Resurrect + War Rage',
    trigger: 'ON_DEATH',
    description: 'Resurrect with HP 2000 + Lv*100 (CD 150s, max 2/game). +10% damage dealt.',
    // Resurrect handled in CombatEngine checkDeath()
    applyToStats(stats) {
      return { ...stats, _damageAmp: 0.10 };
    },
    apply() { return null; },
  },

  'Frost Cape': {
    name: 'Frost Power',
    trigger: 'AFTER_SKILL',
    cooldown: 0.65,
    description: 'After skill: next AA within 5s deals AoE physical 100 + 30% bonus AD, slow 30% for 0.5s',
    nextAABonus: {
      damage: {
        base: 100,
        adScaling: 0.30,
        apScaling: 0,
        damageType: DamageType.PHYSICAL,
      },
      cc: {
        ccType: CCType.SLOW,
        duration: 0.5,
        value: 0.30,
      },
    },
  },

  'Amulet of Longevity': {
    name: 'Longevity',
    trigger: 'PERIODIC',
    tickInterval: 20, // every 2s
    description: 'Heal 1% max HP every 2s. If not hit by hero for 3s, heal 4% instead.',
    apply(actor, target, tick, log, state) {
      const recentlyHit = (tick - (state.lastHitTick || 0)) < 30;
      const healPct = recentlyHit ? 0.01 : 0.04;
      const healAmount = Math.round(actor.maxHP * healPct);
      actor.currentHP = Math.min(actor.maxHP, actor.currentHP + healAmount);
      actor.totalHealing += healAmount;
    },
  },

  'Rock Shield': {
    name: 'Pain Absorb',
    trigger: 'ACTIVE',
    cooldown: 0, // Data incomplete
    description: '[Active] Shield (30% max HP + 10% lost HP) for 3s, usable while CC\'d. -70% AD after.',
    active: {
      effect: 'SHIELD',
      hpPct: 0.30,
      lostHpPct: 0.10,
      duration: 3,
      cooldown: 60,
      name: 'Pain Absorb',
    },
  },

  // ════════════════════════════════════════════════════════════════════════
  // MOVEMENT / BOOTS
  // ════════════════════════════════════════════════════════════════════════

  'Gilded Greaves': {
    name: 'Status Resistance +35%',
    trigger: 'ALWAYS',
    description: 'Reduce CC duration by 35%',
    // Handled in CombatEngine applyCC()
    apply() { return null; },
  },

  'Flashy Boots': {
    name: 'Ultimate CDR -15%',
    trigger: 'ALWAYS',
    description: 'Ultimate cooldown -15%',
    applyToStats(stats) {
      // Applied to ultimate CD specifically
      return { ...stats, _ultCDR: 0.15 };
    },
    apply() { return null; },
  },

  'War Boots': {
    name: 'AA Heal',
    trigger: 'ON_HIT',
    cooldown: 0.3,
    description: 'AA heals 20 HP (half vs non-heroes)',
    apply(actor, target, tick, log, state) {
      actor.currentHP = Math.min(actor.maxHP, actor.currentHP + 20);
      actor.totalHealing += 20;
    },
  },

  "Hermes' Select": {
    name: 'Swift',
    trigger: 'ALWAYS',
    description: '+60 MS out of combat',
    // In combat sim, this doesn't apply (always in combat)
    apply() { return null; },
  },

  // ════════════════════════════════════════════════════════════════════════
  // JUNGLING ITEMS
  // ════════════════════════════════════════════════════════════════════════

  "Monster's Bane": {
    name: 'Stalk',
    trigger: 'ON_JUNGLE_KILL',
    description: 'Jungle kill: +3 AP, +30 max mana (max 20 stacks)',
    // Pre-computed stacks for combat
    apply() { return null; },
  },

  'Gnoll Cleaver': {
    name: 'Stalk',
    trigger: 'ON_JUNGLE_KILL',
    description: 'Jungle kill: +20 HP (max 20 stacks)',
    apply() { return null; },
  },

  'Mr. Stabby': {
    name: 'Stalk',
    trigger: 'ON_JUNGLE_KILL',
    description: 'Jungle kill: +1 AD (max 20 stacks)',
    apply() { return null; },
  },

  'Stormy Bow': {
    name: 'Stalk',
    trigger: 'ON_JUNGLE_KILL',
    description: 'Jungle kill: +0.8% AS (max 20 stacks)',
    apply() { return null; },
  },

  // ════════════════════════════════════════════════════════════════════════
  // SUPPORT ITEMS
  // ════════════════════════════════════════════════════════════════════════

  'Elemental Gem': {
    name: 'Up-and-Coming + Modest',
    trigger: 'PASSIVE',
    description: 'Economy passive, not relevant in 1v1 combat',
    apply() { return null; },
  },

  'EarthGem': {
    name: 'Reward + Genesis',
    trigger: 'PASSIVE',
    description: '+16-30 AD or +32-60 AP. +32-60 Armor & MR.',
    applyToStats(stats) {
      // Average values
      stats.phys_defense += 46;
      stats.magic_defense += 46;
      return stats;
    },
    apply() { return null; },
  },

  'Fire Gem': {
    name: 'Reward + Magical Shadow',
    trigger: 'PASSIVE',
    description: '+16-30 AD or +32-60 AP. +5% CDR, +5% AS.',
    applyToStats(stats) {
      stats.cooldown_reduction += 0.05;
      stats.attack_speed += 0.05;
      return stats;
    },
    apply() { return null; },
  },

  // ════════════════════════════════════════════════════════════════════════
  // SUPPORT T3 ITEMS
  // ════════════════════════════════════════════════════════════════════════

  'Barrier': { apply() { return null; } },
  'Magic Eye': { apply() { return null; } },
  'Accelerate': { apply() { return null; } },
  'Cleansing': { apply() { return null; } },
  'Rejuvenate': { apply() { return null; } },
};

// ============================================================================
// HELPER: Get passive definition for an item
// ============================================================================

export function getItemPassive(itemName) {
  return ITEM_PASSIVES[itemName] || null;
}

// ============================================================================
// HELPER: Attach passive definitions to items array
// ============================================================================

export function attachPassivesToItems(items) {
  return items.map(item => {
    if (!item) return item;
    const passiveDef = ITEM_PASSIVES[item.name || item.item_name];
    if (passiveDef) {
      return {
        ...item,
        passive: passiveDef,
        active: passiveDef.active || null,
      };
    }
    const inferredPassive = inferItemPassiveFromText(item.passive, item.name || item.item_name);
    if (inferredPassive) {
      return {
        ...item,
        passive: inferredPassive,
      };
    }
    return item;
  });
}

// ============================================================================
// HELPER: Apply "ALWAYS" passive stat modifications
// ============================================================================

export function applyAlwaysPassiveStats(items, baseStats) {
  let stats = { ...baseStats };
  
  for (const item of items) {
    if (!item) continue;
    const passiveDef = ITEM_PASSIVES[item.name || item.item_name];
    if (passiveDef && passiveDef.applyToStats) {
      stats = passiveDef.applyToStats(stats);
    }
  }
  
  return stats;
}
